import { Component, computed, inject, input } from '@angular/core'
import { CommonModule } from '@angular/common'
import { RouterModule } from '@angular/router'
import { EMPTY, catchError } from 'rxjs'
import { NavigationMenuProgram } from '../../entities/navigation-menu-item.entity'
import { SidebarService } from 'src/app/services/sidebar.service'
import { InternationalizationService } from 'src/app/services/internationalization.service'

@Component({
  selector: 'app-sidebar-nav-group',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar-nav-group.component.html',
  styleUrl: './sidebar-nav-group.component.scss',
})
export class SidebarNavGroupComponent {
  private readonly sidebarService = inject(SidebarService)
  private readonly translateService = inject(InternationalizationService)

  readonly item = input.required<NavigationMenuProgram>()
  readonly depth = input<number>(0)
  readonly siblingIds = input<number[]>([])
  readonly favoriteIds = input<Set<number>>(new Set())

  readonly hasChildren = computed(() => {
    const children = this.item().children
    return !!children && children.length > 0
  })

  readonly isExpanded = computed(() => this.sidebarService.isExpanded(this.item().id))

  readonly childDepth = computed(() => this.depth() + 1)

  readonly label = computed(() => {
    const item = this.item()
    const raw = item.displayNameCode ?? item.displayName ?? item.systemName ?? ''
    return this.translateService.getTranslation(raw)
  })

  readonly isFavorited = computed(() => this.favoriteIds().has(this.item().id))

  readonly hasValidRoute = computed(() => {
    const item = this.item()
    return !!(item.angularPath?.trim() || item.systemName?.trim())
  })

  protected toggleExpand(): void {
    if (this.hasChildren()) {
      this.sidebarService.toggleExpanded(this.item().id, this.siblingIds())
    }
  }

  protected childIds(children: NavigationMenuProgram[] | undefined): number[] {
    return children?.map((child) => child.id) ?? []
  }

  protected toggleFavorite(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()

    const program = this.item()

    if (this.isFavorited()) {
      this.sidebarService.favoritePrograms.update((list) =>
        list.filter((f) => f.id !== program.id)
      )
      this.sidebarService
        .deleteFavorite(program.id)
        .pipe(
          catchError(() => {
            this.sidebarService.favoritePrograms.update((list) => [program, ...list])
            return EMPTY
          })
        )
        .subscribe()
    } else {
      this.sidebarService.favoritePrograms.update((list) => [program, ...list])
      this.sidebarService
        .addFavorite(program.id)
        .pipe(
          catchError(() => {
            this.sidebarService.favoritePrograms.update((list) =>
              list.filter((f) => f.id !== program.id)
            )
            return EMPTY
          })
        )
        .subscribe()
    }
  }
}
